
import { 
  Shield, 
  Search, 
  FileCheck, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  BarChart
} from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const VulnerabilityManagement = () => {
  return (
    <LandingPageTemplate
      title="Vulnerability Management Services"
      description="Enterprise-grade vulnerability assessment and management to identify, prioritize, and remediate security weaknesses across your infrastructure."
      keywords={[
        "vulnerability management",
        "vulnerability assessment",
        "security scanning",
        "risk remediation",
        "cybersecurity",
        "threat detection",
        "compliance"
      ]}
      canonicalUrl="/cybersecurity/vulnerability-management/"
      schemaType="service"
      schemaData={{
        name: "Vulnerability Management Services",
        description: "Comprehensive vulnerability assessment and management program with continuous monitoring and remediation tracking.",
        provider: {
          name: "edgefi",
          url: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Proactive Protection with"
        highlighted="Vulnerability Management"
        description="Stay ahead of threats with continuous vulnerability assessment, risk-based prioritization, and remediation tracking across your entire infrastructure."
        ctaText="Schedule Assessment"
        secondaryButtonText="View Process"
        secondaryButtonLink="#process"
        heroPattern="grid"
        modalTitle="Request Vulnerability Assessment"
        modalDescription="Tell us about your environment and security needs."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Vulnerability Management",
          service_interest: "Security Assessment"
        }}
      />

      <FeatureSection 
        id="features"
        title="Comprehensive Vulnerability Management"
        subtitle="KEY CAPABILITIES"
        description="Our enterprise-grade vulnerability management program helps identify, classify, prioritize, and track security weaknesses across your infrastructure."
        features={[
          {
            title: "Continuous Assessment",
            description: "Regular automated scans and manual testing to identify vulnerabilities across networks, applications, and systems.",
            icon: <Search className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk-Based Prioritization",
            description: "Advanced risk scoring and business context analysis to prioritize vulnerabilities based on threat level and impact.",
            icon: <AlertCircle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Compliance Reporting",
            description: "Detailed reporting and dashboards mapped to major compliance frameworks including NIST, ISO, and CIS.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Remediation Tracking",
            description: "End-to-end workflow management to track remediation progress and verify security fixes with clear metrics.",
            icon: <CheckCircle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Threat Intelligence",
            description: "Integration with threat feeds to correlate vulnerabilities with active exploits and emerging threats.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Performance Metrics",
            description: "Track key metrics like mean time to remediate (MTTR) and risk reduction over time with executive dashboards.",
            icon: <BarChart className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        id="process"
        title="Our Approach"
        subtitle="METHODOLOGY"
        description="A systematic and proven approach to managing vulnerabilities across your organization"
        steps={[
          {
            title: "Asset Discovery",
            description: "Comprehensive mapping of all assets including networks, systems, applications, and cloud resources"
          },
          {
            title: "Assessment",
            description: "Continuous scanning combined with manual testing to identify vulnerabilities"
          },
          {
            title: "Risk Analysis",
            description: "Advanced risk scoring considering threat level, business impact, and exploit likelihood"
          },
          {
            title: "Remediation",
            description: "Prioritized remediation planning with clear ownership and timelines"
          },
          {
            title: "Verification",
            description: "Post-remediation testing and ongoing monitoring to verify fixes"
          },
          {
            title: "Reporting",
            description: "Regular reporting on program metrics, risk trends, and compliance status"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our Vulnerability Management"
        subtitle="BUSINESS VALUE"
        background="gradient"
        benefits={[
          {
            title: "Reduced Risk Exposure",
            description: "Proactively identify and address security weaknesses before they can be exploited"
          },
          {
            title: "Compliance Ready",
            description: "Meet regulatory requirements with comprehensive vulnerability management documentation"
          },
          {
            title: "Cost Effective",
            description: "Prioritize remediation efforts based on risk impact and business context"
          },
          {
            title: "Time Savings",
            description: "Automated scanning and reporting saves valuable IT resources"
          },
          {
            title: "Clear Metrics",
            description: "Track and demonstrate security improvements with executive-ready metrics"
          },
          {
            title: "Expert Support",
            description: "Access to security experts for vulnerability analysis and remediation guidance"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Strengthen Your Security Posture?"
        description="Schedule a consultation to discover how our vulnerability management service can protect your assets."
        ctaText="Get Started"
        background="purple"
        modalTitle="Request Vulnerability Management Consultation"
        modalDescription="Tell us about your vulnerability management needs and challenges."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Vulnerability Management",
          service_interest: "Security Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default VulnerabilityManagement;
